import { z } from "zod";

import type { ContractBlockKind } from "./contractContent";

/**
 * İKİ VERSİYONUN KARŞILAŞTIRILMASI (CLOS-140 `[095]`).
 *
 * ### ⚠ Eşleme KİMLİKLE, sırayla değil
 *
 * `[086]` belge biçiminde madde `id`'si istemcinin ürettiği KALICI kimliktir ve şemasının
 * kendi ifadesiyle *"madde taşınınca da aynı kalıyor, sıra numarası değil"*. Bu yüzden
 * eşleme için metin benzerliğine dayalı sezgisel bir yöntem gerekmiyor: aynı kimlik aynı
 * maddedir. Sezgisel eşleme, iki maddesi birbirine benzeyen bir sözleşmede yanlış çift
 * kurar ve kullanıcıya olmayan bir değişiklik gösterirdi.
 *
 * Taşınma bu sayede bedava geliyor: kimlik aynı, içerik aynı, sıra farklı.
 *
 * ### ⚠ Biçim değişikliği içerik değişikliğinden AYRI
 *
 * Kart: *"biçim değişikliği içerik değişikliğiyle KÖR BİÇİMDE karıştırılmaz."* Metni aynı
 * kalıp yalnız `paragraph` → `bullet` olan bir blok, silinmiş + eklenmiş gibi
 * gösterilseydi kullanıcı sözleşme metninin değiştiğini sanırdı.
 */

/** Sonucun üretildiği algoritma sürümü — cache anahtarının ve sonuç kimliğinin parçası. */
export const CONTRACT_COMPARISON_ALGORITHM_VERSION = 1;

export const CLAUSE_CHANGE_KINDS = [
  "added",
  "removed",
  "modified",
  "moved",
  "moved_modified",
  "unchanged",
] as const;
export type ClauseChangeKind = (typeof CLAUSE_CHANGE_KINDS)[number];

export const CLAUSE_CHANGE_NAMES: Record<ClauseChangeKind, string> = {
  added: "Eklendi",
  removed: "Silindi",
  modified: "Değişti",
  moved: "Taşındı",
  moved_modified: "Taşındı ve değişti",
  unchanged: "Aynı",
};

export const BLOCK_CHANGE_KINDS = ["added", "removed", "format_changed", "unchanged"] as const;
export type BlockChangeKind = (typeof BLOCK_CHANGE_KINDS)[number];

export const BLOCK_CHANGE_NAMES: Record<BlockChangeKind, string> = {
  added: "Eklendi",
  removed: "Silindi",
  format_changed: "Biçimi değişti",
  unchanged: "Aynı",
};

/**
 * Karşılaştırmanın bir tarafı.
 *
 * ⚠ `contentHash` sonuçta TAŞINIYOR: kart *"sonuç ... bütünlük referanslarını açıkça taşır;
 * sonuç başka sürüme yanlış bağlanmaz"* diyor. Yalnız `versionId` taşınsaydı, aynı kimlikli
 * bir satırın içeriği bir gün başka bir yoldan değişse sonuç sessizce yanlış sürüme ait
 * olurdu.
 */
export type ContractComparisonSide = {
  versionId: string;
  versionNumber: number;
  contentHash: string;
};

export type ContractBlockChange = {
  change: BlockChangeKind;
  kind: ContractBlockKind;
  text: string;
  /** Yalnız `format_changed`: metin aynı kalıp blok türü değiştiğinde ÖNCEKİ tür. */
  previousKind?: ContractBlockKind;
};

export type ContractClauseChange = {
  clauseId: string;
  change: ClauseChangeKind;
  /** Solda kaçıncı sırada (yoksa `null` — eklenmiş madde). */
  leftIndex: number | null;
  /** Sağda kaçıncı sırada (yoksa `null` — silinmiş madde). */
  rightIndex: number | null;
  /** Sağdaki başlık; silinmiş maddede soldaki. */
  title: string;
  /** Başlık değiştiyse öncekisi. */
  previousTitle?: string;
  /**
   * Blok düzeyi fark — yalnız içeriği değişmiş maddelerde dolu.
   *
   * Değişmemiş ve yalnız taşınmış maddelerde BOŞ: madde bazında zaten "aynı" denmişken
   * her bloğu tek tek "aynı" diye taşımak, sonucu belge boyutunda büyütür ve okuyan
   * tarafa hiçbir şey söylemez.
   */
  blocks: ContractBlockChange[];
};

export type ContractComparisonSummary = {
  added: number;
  removed: number;
  modified: number;
  moved: number;
  unchanged: number;
};

export type ContractComparison = {
  algorithmVersion: number;
  left: ContractComparisonSide;
  right: ContractComparisonSide;
  summary: ContractComparisonSummary;
  clauses: ContractClauseChange[];
};

/**
 * Karşılaştırmanın YAPILAMADIĞI durumlar — makine tarafından okunabilir sebep.
 *
 * Kart: *"eksik içerik, bozuk bütünlük, desteklenmeyen format, timeout, iptal, erişim
 * kaybı ve çok büyük belge durumları kontrollü hata verir."* Arayüz her sebebe farklı
 * karşılık veriyor (metin çıkarma öner / versiyon seçtir / uyar), bu yüzden tek bir
 * cümle yerine kod gönderiliyor (`docs/api-hata-sozlesmesi.md`).
 */
export const CONTRACT_COMPARISON_REJECTIONS = [
  /** İki taraf da aynı versiyon — karşılaştıracak bir şey yok. */
  "same_version",
  /** Versiyonun uygulama içi metni yok (dosyadan doğdu, metin henüz çıkarılmadı). */
  "content_missing",
  /** Kaynak içerik kaldırılmış; tarihsel satır duruyor ama metni okunamıyor. */
  "content_purged",
  /** Belge, ölçülmüş sınırın üstünde. */
  "too_large",
] as const;
export type ContractComparisonRejection = (typeof CONTRACT_COMPARISON_REJECTIONS)[number];

export const CONTRACT_COMPARISON_REJECTION_NAMES: Record<ContractComparisonRejection, string> = {
  same_version: "Karşılaştırma için iki farklı versiyon seçilmelidir",
  content_missing: "Bu versiyonun uygulama içi metni yok; önce belgeden metin çıkarılmalı",
  content_purged: "Bu versiyonun kaynak içeriği kaldırılmış; karşılaştırılamıyor",
  too_large: "Belge karşılaştırma için çok büyük",
};

/**
 * Karşılaştırma sorgusu: hangi iki versiyon.
 *
 * ⚠ Versiyonlar QUERY'de, yolda değil. Yol `/:id/versions/compare` biçiminde ve `compare`
 * statik bir segment: iki kimliği yola koysaydık `/:id/versions/:a/:b` gibi bir kalıp
 * gerekirdi ve o kalıp mevcut detay ucuyla (`/:id/versions/:versionId`) karışırdı.
 */
export const contractComparisonQuerySchema = z.object({
  left: z.uuid("Geçerli bir versiyon kimliği gereklidir"),
  right: z.uuid("Geçerli bir versiyon kimliği gereklidir"),
});
export type ContractComparisonQuery = z.infer<typeof contractComparisonQuerySchema>;
